import { AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, OnDestroy, OnInit, signal, untracked, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { BridgeMessages, ViewType } from '@ortho-next/nextray-core';
import { of, switchMap } from 'rxjs';
import { ReferenceTypeEnum, RingSize, ToastService } from '../../core';
import { IImageCalibrationControl, ImageCalibrationForm, imgCalibErrorMsgs, RingCalibrationInfo, setImgCalibValidators } from '../../models';
import { CanvasService, TlhexService } from '../../services';
import { BaseComponent, ConfirmationComponent, FormValidationMsgDirective, RingSelectionComponent, ValidationMessagePipe } from '../../shared';


/**
 * Component for image calibration on image garage modal.
 */
@Component({
	selector: 'image-calibration',
	templateUrl: './image-calibration.component.html',
	imports: [
		AsyncPipe,
		NgbTooltip,
		FormsModule,
		ReactiveFormsModule,
		ValidationMessagePipe,
		RingSelectionComponent,
		FormValidationMsgDirective
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class ImageCalibrationComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

	private readonly modalSrv = inject(NgbModal);
	private readonly toastSrv = inject(ToastService);
	private readonly tlhexSrv = inject(TlhexService);
	private readonly canvasSrv = inject(CanvasService);

	form = input<FormGroup<IImageCalibrationControl>>();
	ringCalibData = input<RingCalibrationInfo | null>(null);
	isResetAnalysisWarning = input(false);

	calibrationInput = viewChild<ElementRef<HTMLInputElement>>('calibrationInput');
	isRingsToolVisible = computed(() => !!this.ringCalibData());
	ringsList = toSignal(
		toObservable(this.isRingsToolVisible).pipe(
			switchMap(i => i ? this.tlhexSrv.getRingSizes() : of([]))
		)
	);
	disabled = signal(false);
	ringToolDisabled = signal(false);
	ringSelectDisabled = signal(false);
	initProxRing?: RingSize;
	initDistRing?: RingSize;
	referenceEnum = ReferenceTypeEnum;

	readonly errorMsgs = imgCalibErrorMsgs;
	readonly helpImgLine = 'images/gif/line.gif';
	readonly helpImgCircle = 'images/gif/circle.gif';
	helpImgRing = '';

	private readonly initRings = effect(() => this._initRings());

	ngOnInit(): void {
		this.canvasSrv.addEventListener('onResult', this.calibToolInsertError);
		this.initForm();
		this.helpImgRing = this._helpImgRing;
	}

	ngAfterViewInit(): void {
		this.calibrationInput()?.nativeElement.addEventListener('wheel', this.disableScroll);
		this.calibrationInput()?.nativeElement.addEventListener('keydown', this.handleKeyPress);
	}

	private readonly disableScroll = (event: Event) => event.preventDefault();

	private readonly handleKeyPress = (event: KeyboardEvent) => {
		if (event.key == 'ArrowUp' || event.key == 'ArrowDown') {
			event.preventDefault();
		}
	};

	/**
	* Event listener for calibration tool insertion error.
	*/
	calibToolInsertError = (event: { args: keyof BridgeMessages }) => {
		if (event.args == 'calibrationCircleRadiusError') {
			this.toastSrv.showWarning(this.labels().IMAGE_CALIBRATION_COMPONENT_CIRCLE_RADIUS_ERROR);
		} else if (event.args == 'calibrationEllipseRadiusError') {
			this.toastSrv.showWarning(this.labels().IMAGE_CALIBRATION_COMPONENT_ELLIPSE_RADIUS_ERROR);
		} else if (event.args == 'calibrationCirclePointsError') {
			this.form()!.controls.calibrationTool.setValue(null);
		}
	};

	private _initRings(): void {
		if (this.ringsList()?.length) {
			untracked(() => {
				if (!!this.ringCalibData()!.proximalRingId && !!this.ringCalibData()!.distalRingId) {
					this.setRings();
					this.form()!.controls.calibrationTool.setValue('rings');
					this.initProxRing = this.ringsList()!.find(r => r.id === this.ringCalibData()!.proximalRingId);
					this.updateProxRing(this.initProxRing);
					this.initDistRing = this.ringsList()!.find(r => r.id === this.ringCalibData()!.distalRingId);
					this.updateDistRing(this.initDistRing);
					this.disabled.set(true);
				} else if (this.ringCalibData()?.preventRingCalibration) {
					this.ringToolDisabled.set(true);
				}
			});
		}
	}

	private initForm(): void {
		setImgCalibValidators(this.form()!);
		this.form()!.controls.calibrationTool.setValue(null);
		this.form()!.controls.calibrationValue.setValue(null);
		this.form()!.markAsUntouched();
	}

	/**
	 * Check if registering mode is active.
	 */
	get isPointsRegistering(): boolean {
		return this.canvasSrv.isPointsRegistering;
	}

	/**
	 * Prevent use of scientific notation characters.
	 */
	preventScientificNotation(event: KeyboardEvent | Event): void {
		if (event instanceof KeyboardEvent && ['e', 'E', '+', '-'].includes(event.key)) {
			event.preventDefault();
		}
		const val = (event.target as HTMLInputElement).value;
		if (!val || val.includes('e') || val.includes('E') || val.includes('+') || val.includes('-')) {
			(event.target as HTMLInputElement).value = '';
		}
	}

	/**
	 * Insert line calibration tool.
	 */
	setLine(): void {
		this.canvasSrv.dispatch('startCalibration');
		this.canvasSrv.dispatch('calibByLine');
		this.reset();
	}

	/**
	 * Insert circle calibration tool.
	 */
	setCircle(): void {
		this.canvasSrv.dispatch('startCalibration');
		this.canvasSrv.dispatch('calibByCircle');
		this.reset();
	}

	/**
	 * Insert rings calibration tool.
	 */
	setRings(): void {
		this.canvasSrv.dispatch('startCalibration');
		this.canvasSrv.dispatch('calibByEllipse');
		this.canvasSrv.dispatch('setRingCalibrationData', {
			viewType: this.ringCalibData()!.viewType,
			boneType: this.ringCalibData()!.boneType,
			refType: this.ringCalibData()!.refType,
			side: this.ringCalibData()!.side
		});
		const oldProxRing = this.form()!.controls.proximalRing.value;
		const oldDistRing = this.form()!.controls.distalRing.value;
		this.reset();
		// Proximal ring
		if (oldProxRing) {
			this.updateProxRing(oldProxRing);
		} else if (this.ringCalibData()?.checkupProximalRingId) {
			this.initProxRing = this.ringsList()!.find(r => r.id === this.ringCalibData()!.checkupProximalRingId);
			this.updateProxRing(this.initProxRing);
			this.ringCalibData()?.checkupReadonly && this.ringSelectDisabled.set(true);
		}
		// Distal ring
		if (oldDistRing) {
			this.updateDistRing(oldDistRing);
		} else if (this.ringCalibData()?.checkupDistalRingId) {
			this.initDistRing = this.ringsList()!.find(r => r.id === this.ringCalibData()!.checkupDistalRingId);
			this.updateDistRing(this.initDistRing);
			this.ringCalibData()?.checkupReadonly && this.ringSelectDisabled.set(true);
		}
	}

	private reset(): void {
		this.form()!.controls.calibrationValue.setValue(null);
		setTimeout(() => this.calibrationInput()?.nativeElement.focus(), 1);
		this.form()!.controls.proximalRing.reset();
		this.form()!.controls.distalRing.reset();
	}

	private get _helpImgRing(): string {
		if (this.ringCalibData()?.viewType === ViewType.LT) {
			return this.ringCalibData()?.refType === ReferenceTypeEnum.Distal ? RingImageEnum.distalLT : RingImageEnum.proximalLT;
		} else {
			return this.ringCalibData()?.refType === ReferenceTypeEnum.Distal ? RingImageEnum.distalAP : RingImageEnum.proximalAP;
		}
	}

	/**
	 * Set circle by input points.
	 */
	setCircleByPoints(points: { x: number; y: number; }[]): void {
		this.form()!.controls.calibrationTool.setValue('circle');
		setTimeout(() => this.calibrationInput()?.nativeElement.focus(), 1);
		this.canvasSrv.dispatch('startCalibration');
		this.canvasSrv.dispatch('setCalibrationCircleFromPoints', points);
	}

	/**
	 * Reset all points.
	 */
	resetTool(): void {
		switch (this.form()!.value.calibrationTool) {
			case 'line': return this.setLine();
			case 'circle': return this.setCircle();
			case 'rings': return this.setRings();
			default: return this.reset();
		}
	}

	//#region RINGS

	/**
	 * Select proximal ring.
	 */
	selectProxRing(ring?: RingSize): void {
		if (this.ringCalibData()?.checkupProximalRingId && this.ringCalibData()!.checkupProximalRingId !== ring?.id) {
			this.openP2PRingsModal(ring, 'proximal');
		} else {
			this.updateProxRing(ring);
		}
	}

	private updateProxRing(ring?: RingSize): void {
		this.form()!.controls.proximalRing.setValue(ring ?? null);
		this.updateRingCalibrationValue();
	}

	private restoreProxRing(): void {
		const ring = this.form()!.controls.proximalRing.value;
		this.initProxRing = ring ? { ...ring } : undefined;
	}

	/**
	 * Select distal ring.
	 */
	selectDistRing(ring?: RingSize): void {
		if (this.ringCalibData()?.checkupDistalRingId && this.ringCalibData()!.checkupDistalRingId !== ring?.id) {
			this.openP2PRingsModal(ring, 'distal');
		} else {
			this.updateDistRing(ring);
		}
	}

	private updateDistRing(ring?: RingSize): void {
		this.form()!.controls.distalRing.setValue(ring ?? null);
		this.updateRingCalibrationValue();
	}

	private restoreDistRing(): void {
		const ring = this.form()!.controls.distalRing.value;
		this.initDistRing = ring ? { ...ring } : undefined;
	}

	private updateRingCalibrationValue(): void {
		const formValue = this.form()!.value as ImageCalibrationForm;
		const ref = this.ringCalibData()!?.refType;
		const ring = ref == this.referenceEnum.Proximal ? formValue.proximalRing : formValue.distalRing;
		const oppositeRing = ref == this.referenceEnum.Proximal ? formValue.distalRing : formValue.proximalRing;
		if (ref && ring && oppositeRing) {
			const value = Number.parseInt(ring.description.split('mm')[0]);
			this.form()!.controls.calibrationValue.setValue(value);
			const oppositeRingType = oppositeRing.ringType;
			this.canvasSrv.dispatch('setRing', { ringModel: { ...ring, type: ref === ReferenceTypeEnum.Proximal ? 'proximal' : 'distal' }, oppositeRingType });
		} else {
			this.canvasSrv.dispatch('setRing');
		}
	}

	private openP2PRingsModal(ring?: RingSize, type: 'proximal' | 'distal' = 'proximal'): void {
		const modalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: this.labels().IMAGE_CALIBRATION_COMPONENT_RINGS_MODAL_TITLE,
			message: this.labels().IMAGE_CALIBRATION_COMPONENT_RINGS_MODAL_MESSAGE,
			cancelBtn: this.labels().IMAGE_CALIBRATION_COMPONENT_RINGS_MODAL_NO,
			confirmBtn: this.labels().IMAGE_CALIBRATION_COMPONENT_RINGS_MODAL_YES
		};
		modalRef.result.then(_ => {
			type === 'proximal' ? this.updateProxRing(ring) : this.updateDistRing(ring);
		}, _ => {
			type === 'proximal' ? this.restoreProxRing() : this.restoreDistRing();
		}
		).catch(error => false);
	}

	//#endregion

	ngOnDestroy(): void {
		this.calibrationInput()?.nativeElement.removeEventListener('wheel', this.disableScroll);
		this.calibrationInput()?.nativeElement.removeEventListener('keydown', this.handleKeyPress);
		this.canvasSrv.removeEventListener('onResult', this.calibToolInsertError);
		this.canvasSrv.dispatch('resetCalibration');
		this.reset();
	}

}

enum RingImageEnum {
	proximalAP = 'images/gif/ring_proximal_AP.gif',
	proximalLT = 'images/gif/ring_proximal_LAT.gif',
	distalAP = 'images/gif/ring_distal_AP.gif',
	distalLT = 'images/gif/ring_distal_LAT.gif',
}
